import { Descriptions, Drawer, Spin, Table, Tag, Typography } from 'antd'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  platformGetProviderOption,
  platformListProviderOptionCompatibleRuntimes,
  type ProviderOptionAdminRow,
} from '../api'

type CompatibleRuntimeRow = {
  worker_id: string
  worker_name: string
  online?: boolean
  worker_status?: string
  capability_ready?: boolean
  worker_software_id?: string | null
  runtime_id?: string | null
}

type Props = {
  optionId: string | null
  open: boolean
  onClose: () => void
}

function isWorkerBacked(row: ProviderOptionAdminRow | null): boolean {
  if (!row) return false
  if (row.worker_backed_scheme) return true
  const t = row.mapping?.execution_target_type
  return t === 'LOCAL_WORKER' || t === 'BROWSER_PROVIDER'
}

export default function ExecutionPlanDetailDrawer({ optionId, open, onClose }: Props) {
  const [loading, setLoading] = useState(false)
  const [row, setRow] = useState<ProviderOptionAdminRow | null>(null)
  const [runtimes, setRuntimes] = useState<CompatibleRuntimeRow[]>([])
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open || !optionId) {
      setRow(null)
      setRuntimes([])
      setError(null)
      return
    }
    let cancelled = false
    setLoading(true)
    setError(null)
    Promise.all([
      platformGetProviderOption(optionId),
      platformListProviderOptionCompatibleRuntimes(optionId).catch(() => ({ data: { items: [] } })),
    ])
      .then(([optRes, rtRes]) => {
        if (cancelled) return
        setRow(optRes.data)
        setRuntimes(
          Array.isArray(rtRes.data.items) ? (rtRes.data.items as CompatibleRuntimeRow[]) : [],
        )
      })
      .catch(() => {
        if (!cancelled) setError('加载执行方案详情失败')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [open, optionId])

  const m = row?.mapping
  const workerBacked = isWorkerBacked(row)

  return (
    <Drawer
      title="执行方案详情"
      width={520}
      open={open}
      onClose={onClose}
      destroyOnClose
      data-testid="execution-plan-detail-drawer"
    >
      {loading && <Spin />}
      {error && <Typography.Text type="danger">{error}</Typography.Text>}
      {row && !loading && (
        <>
          <Descriptions column={1} size="small" bordered style={{ marginBottom: 16 }}>
            <Descriptions.Item label="方案 ID">{row.id}</Descriptions.Item>
            <Descriptions.Item label="方案名称">{row.frontend_display_name}</Descriptions.Item>
            {row.worker_backed_scheme && row.resolved_worker_display_name && (
              <Descriptions.Item label="Provider（Worker）">{row.resolved_worker_display_name}</Descriptions.Item>
            )}
            <Descriptions.Item label="功能说明">{row.frontend_description || '—'}</Descriptions.Item>
            <Descriptions.Item label="类型">
              {m?.execution_target_type || '—'}
              {workerBacked ? ' · Worker' : m?.execution_target_type === 'EXTERNAL_API' ? ' · 外部 API' : ''}
            </Descriptions.Item>
            <Descriptions.Item label="节点启用">
              <Tag color={row.enabled ? 'green' : 'default'}>{row.enabled ? 'YES' : 'NO'}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="默认方案">
              <Tag color={row.is_default ? 'blue' : 'default'}>{row.is_default ? '当前默认' : '否'}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="排序">{row.sort_order}</Descriptions.Item>
            <Descriptions.Item label="运行状态">
              {m?.runtime_status?.label ? (
                <Tag color={m.runtime_status.tone === 'success' ? 'green' : 'default'}>{m.runtime_status.label}</Tag>
              ) : (
                '—'
              )}
            </Descriptions.Item>
            <Descriptions.Item label="创建时间">{row.created_at || '—'}</Descriptions.Item>
            <Descriptions.Item label="更新时间">{row.updated_at || '—'}</Descriptions.Item>
          </Descriptions>

          {workerBacked && (
            <Typography.Title level={5} style={{ marginTop: 0 }}>Worker 执行</Typography.Title>
          )}
          {workerBacked && (
            <Descriptions column={1} size="small" bordered style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Worker 显示名称">
                {row.resolved_worker_display_name || m?.worker_name || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Worker ID">{row.resolved_worker_id || m?.worker_id || '—'}</Descriptions.Item>
              <Descriptions.Item label="Provider">
                {m?.provider_display_name || m?.provider_key || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Provider Key">{m?.provider_key || '—'}</Descriptions.Item>
              <Descriptions.Item label="Capability">
                {m?.required_worker_capability || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Worker Software">
                {m?.required_worker_software_id || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Runtime 摘要">
                {m?.worker_runtime_availability?.status || '—'}
                {m?.worker_runtime_availability?.ready_runtimes != null
                  ? ` · ready ${m.worker_runtime_availability.ready_runtimes}`
                  : ''}
              </Descriptions.Item>
              {(row.resolved_worker_id || m?.worker_id) && (
                <Descriptions.Item label="Worker 管理">
                  <Link to="/platform/workers">在 Worker 节点查看</Link>
                </Descriptions.Item>
              )}
            </Descriptions>
          )}

          {m?.execution_target_type === 'EXTERNAL_API' && (
            <>
              <Typography.Title level={5} style={{ marginTop: 0 }}>外部 API</Typography.Title>
              <Descriptions column={1} size="small" bordered style={{ marginBottom: 16 }}>
                <Descriptions.Item label="Provider">{m.provider_display_name || m.provider_key || '—'}</Descriptions.Item>
                <Descriptions.Item label="Provider Key">{m.provider_key || '—'}</Descriptions.Item>
                <Descriptions.Item label="Model">{m.model_display_name || m.model_external_id || '—'}</Descriptions.Item>
                <Descriptions.Item label="Model ID">{m.model_external_id || '—'}</Descriptions.Item>
                <Descriptions.Item label="Credential">
                  {m.credential_status?.resolved
                    ? `${m.credential_status.credential_name || '已配置'} · ${m.credential_status.health || m.credential_status.status || ''}`
                    : m.credential_status?.reason_code || '未配置 / 不可用'}
                </Descriptions.Item>
                <Descriptions.Item label="Adapter">{m.adapter_key || '—'}</Descriptions.Item>
              </Descriptions>
            </>
          )}

          {workerBacked && runtimes.length > 0 && (
            <>
              <Typography.Title level={5}>兼容 Runtime</Typography.Title>
              <Table
                size="small"
                pagination={false}
                rowKey="worker_id"
                dataSource={runtimes}
                columns={[
                  { title: 'Worker', dataIndex: 'worker_name', key: 'name' },
                  { title: 'ID', dataIndex: 'worker_id', key: 'id', ellipsis: true },
                  {
                    title: '在线',
                    key: 'online',
                    render: (_, r) => (r.online ? <Tag color="green">在线</Tag> : <Tag>离线</Tag>),
                  },
                  {
                    title: '可执行',
                    key: 'ready',
                    render: (_, r) => (r.capability_ready ? <Tag color="blue">ready</Tag> : <Tag>—</Tag>),
                  },
                ]}
              />
            </>
          )}
        </>
      )}
    </Drawer>
  )
}
